import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import Spinner from '@dashboard/components/Spinner';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import Form from '@dashboard/components/Formik/Form';
import Lozenge from '@dashboard/components/Lozenge';
import Tooltip from '@dashboard/components/Tooltip';
import { useToast } from '@dashboard/context/ToastContext';
import Card from '@dashboard/components/Card';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CardContent from '@dashboard/components/CardContent';
import SettingsDivider from '@dashboard/components/SettingsDivider';
import { teamMembersKey } from '@dashboard/lib/queryKeys';
import { confirm } from '@dashboard/lib/confirm';
import SettingsDescription from '@dashboard/components/SettingsDescription';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import SettingsContainer from '@/dashboard/components/SettingsContainer';

const SettingsMembers = () => {
  const { isOwner, verifyAuth, isAdminLoggedInAsSeller } = useAuth();

  // refresh auth to ensure data is accurate
  useEffect(() => {
    if (isAdminLoggedInAsSeller) return;

    verifyAuth();
  }, []);

  return (
    <Card variant="root">
      <CardHeader variant="root">
        <div className="flex flex-col max-w-lg">
          <CardHeaderTitle variant="root">Team members</CardHeaderTitle>
          <SettingsDescription className="mt-2">
            These are the people who have access to your account. Team owners can add more team
            members and remove existing ones.
          </SettingsDescription>
        </div>
      </CardHeader>

      <CardContent variant="root" className="mt-4 mb-32">
        <SettingsContainer size="lg">
          <Members />

          {isOwner && (
            <>
              <SettingsDivider />

              <SettingsHeader>Invite team members</SettingsHeader>
              <SettingsDescription>Invite a new team member to your account.</SettingsDescription>

              <InviteMember />
            </>
          )}
        </SettingsContainer>
      </CardContent>
    </Card>
  );
};

function Members() {
  const queryClient = useQueryClient();
  const { seller, isOwner, userId, logout, verifyAuth, isAdminLoggedInAsSeller, setSeller } =
    useAuth();

  const { getMembers, deleteMember, changeAccountOwner } = useSellerQueries();

  const { data: members, isLoading: isLoadingMembers } = useQuery({
    queryKey: teamMembersKey,
    queryFn: () => getMembers(seller._id),
    enabled: !!seller._id,
  });

  const deleteMemberMutation = useMutation({
    mutationFn: deleteMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamMembersKey });
    },
  });

  const changeAccountOwnerMutation = useMutation({
    mutationFn: changeAccountOwner,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: teamMembersKey });
      setSeller(data);

      if (!isAdminLoggedInAsSeller) {
        verifyAuth();
      }
    },
  });

  if (isLoadingMembers) {
    return (
      <div>
        <Spinner />
      </div>
    );
  }

  const isAccountOwner = (isOwner && userId === seller.owner) || isAdminLoggedInAsSeller;

  return (
    <div>
      <ul role="list" className="divide-y divide-gray-100">
        {members?.map(member => (
          <li key={member._id} className="flex py-4">
            <div>
              <div className="font-medium text-black space-x-1">
                <span>
                  {member.user.name}
                  {member.user._id === userId && <span className="text-gray-700"> (You)</span>}
                </span>

                <Tooltip
                  label={
                    member.role === 'owner'
                      ? 'The team owner can perform all actions'
                      : 'Admin users can perform all actions except managing team members'
                  }
                >
                  <Lozenge appearance="default">{member.role}</Lozenge>
                </Tooltip>
              </div>
              <p className="text-gray-700">{member.user.email}</p>
            </div>
            <div className="ml-auto relative">
              {((isOwner && member.role !== 'owner') ||
                (member.user._id === userId && !isOwner)) && (
                <Menu>
                  <MenuButton>
                    <EllipsisHorizontalIcon className="h-5 w-5 text-gray-700" aria-hidden="true" />
                  </MenuButton>
                  <MenuItems className="absolute right-0 mt-2 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg outline-none overflow-hidden z-10">
                    <MenuItem>
                      {({ focus }) => (
                        <button
                          className={clsx(
                            focus ? 'bg-gray-100 text-black' : 'text-gray-700',
                            'block px-4 py-2 w-full text-left whitespace-pre',
                          )}
                          onClick={async () => {
                            const proceed = await confirm(
                              member.user._id === userId
                                ? `Leave your team?`
                                : `Remove ${member.user.name} from your team?`,
                            );

                            if (!proceed) return;

                            deleteMemberMutation.mutate({
                              sellerId: seller._id,
                              userId: member.user._id,
                            });

                            if (member.user._id === userId) {
                              logout();
                            }
                          }}
                        >
                          {member.user._id === userId ? 'Leave team' : 'Remove from team'}
                        </button>
                      )}
                    </MenuItem>

                    {isAccountOwner && (
                      <MenuItem>
                        {({ focus }) => (
                          <button
                            className={clsx(
                              focus ? 'bg-gray-100 text-black' : 'text-gray-700',
                              'block px-4 py-2 w-full text-left whitespace-pre',
                            )}
                            onClick={async () => {
                              const proceed = await confirm(
                                `Transfer ownership of your account to ${member.user.name} (${member.user.email})?`,
                                {
                                  description: `You cannot undo this action.`,
                                },
                              );

                              if (!proceed) return;

                              changeAccountOwnerMutation.mutate({
                                sellerId: seller._id,
                                userId: member.user._id,
                              });
                            }}
                          >
                            Make account owner
                          </button>
                        )}
                      </MenuItem>
                    )}
                  </MenuItems>
                </Menu>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function InviteMember() {
  const { seller } = useAuth();

  const { inviteMember } = useSellerQueries();
  const { emitToast } = useToast();

  const invite = useMutation({
    mutationFn: inviteMember,
    onSuccess: () => {
      emitToast({
        title: 'Invite sent!',
        description: 'Ask your team member to check their email',
        type: 'success',
      });
    },
  });

  return (
    <div>
      <Form
        initialValues={{ email: '', role: 'admin' }}
        onSubmit={async (values, actions) => {
          try {
            const response = await invite.mutateAsync({
              sellerId: seller._id,
              email: values.email,
            });

            actions.resetForm();

            return {
              data: response.data,
            };
          } catch (err) {
            return {
              error: err.message,
            };
          }
        }}
        fields={[
          {
            name: 'email',
            label: 'Email',
            type: 'email',
            placeholder: 'Enter email address',
            required: true,
          },
        ]}
        showState={false}
        submitButton={{
          label: 'Invite',
          appearance: 'primary',
        }}
      />
    </div>
  );
}

export default SettingsMembers;
